import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, setUser } from "../lib/api";
import { toast } from "sonner";
import { Truck, X, ArrowRight } from "lucide-react";

/**
 * Customer-only banner shown on the dashboard inviting the user to apply
 * to become a licensed waste carrier. Upgrades the existing account in
 * place (role -> carrier) via POST /api/users/{id}/become-carrier, then
 * prompts the user to upload their licence on the carrier dashboard.
 *
 * Props:
 *   user      current customer user
 *   onChange  callback fired after a successful upgrade with the fresh user
 */
export default function CarrierUpgradeBanner({ user, onChange }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("binshare_carrier_upgrade_dismissed") === "1"
  );
  const [licenseNo, setLicenseNo] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!user || user.role !== "homeowner" && user.role !== "business") return null;
  if (dismissed && !open) return null;

  const dismiss = () => {
    localStorage.setItem("binshare_carrier_upgrade_dismissed", "1");
    setDismissed(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Please confirm you understand carriers are independent businesses, not Binshare employees.");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await api.post(
        `/users/${user.id}/become-carrier`,
        {
          license_no: licenseNo || null,
          company: user.company || null,
          accepted_carrier_terms: true,
        }
      );
      setUser(data);
      onChange?.(data);
      toast.success("You're now a Licensed Carrier — upload your licence to start bidding.");
      localStorage.removeItem("binshare_carrier_upgrade_dismissed");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upgrade — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      data-testid="carrier-upgrade-banner"
      className="p-5 sm:p-6 rounded-2xl border-[#1B3624] border-2 bg-gradient-to-r from-[#1B3624] to-[#274d33] text-white relative overflow-hidden"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="carrier-upgrade-dismiss"
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {!open ? (
        <div className="flex flex-wrap items-center gap-4 sm:gap-6">
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
            <Truck className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-medium text-base sm:text-lg" style={{ fontFamily: "Outfit" }}>
              Are you a licensed waste carrier?
            </div>
            <p className="text-sm text-white/80 mt-0.5">
              Register here to start bidding on marketplace leads in your area.
            </p>
          </div>
          <Button
            data-testid="carrier-upgrade-open"
            onClick={() => setOpen(true)}
            className="rounded-full bg-white text-[#1B3624] hover:bg-[#F1EDE5]"
          >
            Apply now <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="carrier-upgrade-form">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <div className="font-medium" style={{ fontFamily: "Outfit" }}>Apply to become a Licensed Carrier</div>
              <p className="text-xs text-white/70 mt-0.5">
                Enter your waste-carrier licence number (you'll upload the certificate next).
              </p>
            </div>
          </div>
          <div>
            <Label className="text-xs text-white/80">Waste-carrier licence no.</Label>
            <Input
              data-testid="carrier-upgrade-license"
              value={licenseNo}
              onChange={(e) => setLicenseNo(e.target.value)}
              placeholder="CBDU123456"
              className="rounded-xl mt-1 bg-white text-[#1B3624] placeholder:text-[#6B6965]"
            />
          </div>
          <div className="p-3 rounded-xl bg-white/10 border border-white/20">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-white/90">
              Independent contractor agreement
            </div>
            <p className="text-[11px] text-white/80 mt-1.5 leading-relaxed">
              Binshare is a lead-generation marketplace, not an employer. You operate your own
              independent business, set your own prices, and choose which jobs to bid on. You're
              responsible for your own taxes, insurance and waste-disposal compliance.
            </p>
            <label className="mt-2 flex items-start gap-2 cursor-pointer text-sm text-white">
              <input
                data-testid="carrier-upgrade-terms"
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-white"
              />
              <span className="text-xs">
                I confirm I'm an <strong>independent business or self-employed contractor</strong>, not a Binshare employee.
              </span>
            </label>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="carrier-upgrade-submit"
              type="submit"
              disabled={submitting}
              className="rounded-full bg-white text-[#1B3624] hover:bg-[#F1EDE5]"
            >
              {submitting ? "Upgrading…" : "Switch to Carrier account"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
